// Detalhe de um item em "Saída de linha": cabeçalho, metas, timeline com gráficos
// dos 3 indicadores (faturamento, margem de contribuição, clientes positivados),
// lançamento mensal (inclusive retroativo) e decisões (prorrogar / desativar /
// voltar ao mix) com histórico.
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { isAdmin, isAdminOrGestor } from '../lib/rbac';
import { mesLiberado, textoLiberacao } from '../shared/fechamento';
import { FloatingBack } from '../components/FloatingBack';
import { uploadDiscImage } from '../lib/photos';
import { syncProductPhoto } from '../lib/productLink';
import { buildReportHtml, printReport, prepararFoto, type ReportIndicator } from '../lib/report';
import {
  loadDiscontinuation,
  loadEntries,
  loadEvents,
  loadComments,
  addComment,
  deleteComment,
  upsertEntry,
  updateDiscontinuation,
  deleteDiscontinuation,
  applyDecision,
  planMonthsList,
  monthLabel,
  monthKey,
  DISC_STATUS_LABELS,
  type Discontinuation,
  type DiscEntry,
  type DiscEvent,
  type DiscEventKind,
  type DiscComment,
} from '../lib/discontinuations';

const brl = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const pct = (n: number | null | undefined) =>
  n == null ? '—' : `${n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const numOrNull = (v: string) => (v.trim() === '' ? null : Number(v));

export function SaidaLinhaProduto() {
  const { id } = useParams();
  const { grants, userId, fullName } = useAuth();
  const canManage = grants ? isAdminOrGestor(grants) : false;
  // Só admin pode preencher ANTES do fechamento do mês (ex.: se o fechamento sair mais cedo).
  const podeAntecipar = grants ? isAdmin(grants) : false;
  const [disc, setDisc] = useState<Discontinuation | null>(null);
  const [entries, setEntries] = useState<DiscEntry[]>([]);
  const [events, setEvents] = useState<DiscEvent[]>([]);
  const [comments, setComments] = useState<DiscComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState<DiscEventKind | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  async function reload() {
    if (!id) return;
    const [d, e, ev, cm] = await Promise.all([
      loadDiscontinuation(id),
      loadEntries(id),
      loadEvents(id),
      loadComments(id),
    ]);
    setDisc(d);
    setEntries(e);
    setEvents(ev);
    setComments(cm);
    setLoading(false);
  }

  async function onPickPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !disc) return;
    setUploadingPhoto(true);
    const up = await uploadDiscImage(disc.orgId, disc.id, file);
    if (up.error) {
      setUploadingPhoto(false);
      return alert(`Não foi possível enviar a imagem: ${up.error}`);
    }
    const r = await updateDiscontinuation(disc.id, { photo_url: up.url });
    // Mesmo produto (por código) na Rampa recebe a mesma foto.
    if (up.url) await syncProductPhoto(disc.orgId, disc.codigoInterno, up.url);
    setUploadingPhoto(false);
    if (r.error) return alert(r.error);
    await reload();
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const months = useMemo(
    () => (disc ? planMonthsList(disc.startDate, disc.planMonths) : []),
    [disc],
  );
  const byMonth = useMemo(() => new Map(entries.map((e) => [e.refMonth, e])), [entries]);

  if (loading) return <div className="center-note">Carregando…</div>;
  if (!disc) return <div className="center-note">Item não encontrado. <Link to="/saida-linha">Voltar</Link></div>;

  const inicio = new Date(disc.startDate + 'T00:00:00').toLocaleDateString('pt-BR');
  const thisMonth = monthKey(new Date());

  const serie = (pick: (e: DiscEntry) => number | null) =>
    months.map((m) => ({ m, v: byMonth.has(m) ? pick(byMonth.get(m)!) : null }));

  async function baixarRelatorio() {
    if (!disc) return;
    const pts = (pick: (e: DiscEntry) => number | null) =>
      months.map((m) => ({ label: monthLabel(m), value: byMonth.has(m) ? pick(byMonth.get(m)!) : null }));
    const indicators: ReportIndicator[] = [
      { title: 'Faturamento', meta: disc.metaFaturamento, points: pts((e) => e.faturamento), fmt: brl },
      { title: 'Margem de contribuição (MC [R])', meta: disc.metaMargem, points: pts((e) => e.margemContribuicao), fmt: pct },
      { title: 'Clientes positivados', meta: disc.metaClientes, points: pts((e) => e.clientesPositivados), fmt: (v) => (v == null ? '—' : String(v)) },
      { title: '% Devolução (NFD)', meta: disc.metaNfd, points: pts((e) => e.nfd), fmt: pct, lowerIsBetter: true },
    ];
    const html = buildReportHtml({
      title: disc.name,
      brand: disc.brandName,
      subtitle: `${disc.brandName ?? 'sem marca'}${disc.codigoInterno ? ` · ${disc.codigoInterno}` : ''}`,
      tagline: `Saída de linha — ${DISC_STATUS_LABELS[disc.status]}`,
      photoUrl: await prepararFoto(disc.photoUrl),
      header: [
        { label: 'Marca', value: disc.brandName ?? '—' },
        { label: 'Código', value: disc.codigoInterno ?? '—' },
        { label: 'Início do acompanhamento', value: inicio },
        { label: 'Plano', value: `${disc.planMonths} meses` },
        { label: 'Responsável', value: disc.responsavelNome ?? '—' },
        { label: 'Cobertura de embalagens', value: `${disc.coberturaTempo ?? '—'}${disc.coberturaValor != null ? ` · ${brl(disc.coberturaValor)}` : ''}` },
        ...(disc.motivo ? [{ label: 'Motivo', value: disc.motivo }] : []),
      ],
      indicators,
      tableHead: ['Mês', 'Faturamento', 'MC [R]', 'Clientes', '% Devolução', 'Comentários'],
      tableRows: months.map((m) => {
        const e = byMonth.get(m);
        return [
          monthLabel(m),
          brl(e?.faturamento ?? null),
          pct(e?.margemContribuicao ?? null),
          e?.clientesPositivados != null ? String(e.clientesPositivados) : '—',
          pct(e?.nfd ?? null),
          e?.comentarios ?? '',
        ];
      }),
      sections: [
        {
          title: 'Histórico de decisões',
          items: events.map((ev) => ({
            head: `${EVENT_LABELS[ev.kind]} · ${ev.byName ?? '—'} · ${new Date(ev.createdAt).toLocaleDateString('pt-BR')}`,
            body: ev.comment ?? '',
          })),
        },
        {
          title: 'Comentários',
          items: comments.map((c) => ({
            head: `${c.authorName ?? '—'} · ${new Date(c.createdAt).toLocaleDateString('pt-BR')}`,
            body: c.body,
          })),
        },
      ],
      footerNote: `Gerado em ${new Date().toLocaleString('pt-BR')} · Workflow de Novos Produtos — Trevo Lácteos`,
    }, true);
    printReport(html, `saida-linha-${disc.codigoInterno ?? disc.name}`);
  }

  return (
    <>
      <div className="topbar">
        <div>
          <div className="muted" style={{ fontSize: 12 }}>
            <Link to="/saida-linha">Saída de linha</Link> · {disc.brandName ?? 'sem marca'}
          </div>
          <h1>{disc.name}</h1>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button className="btn-ghost btn-sm" onClick={baixarRelatorio}>📄 Relatório PDF</button>
          <span className={`seal ${disc.status === 'em_acompanhamento' ? 'pend' : 'ok'}`}>
            {DISC_STATUS_LABELS[disc.status]}
          </span>
        </div>
      </div>

      <div className="content">
        {/* Cabeçalho */}
        <div className="card card-pad disc-header">
          <div className="disc-header-imgwrap">
            <div className="disc-header-img">
              {disc.photoUrl ? <img src={disc.photoUrl} alt={disc.name} /> : <span className="disc-noimg">sem foto</span>}
            </div>
            {canManage && (
              <label className="btn-ghost btn-sm" style={{ marginTop: 6, textAlign: 'center', cursor: 'pointer' }}>
                {uploadingPhoto ? 'Enviando…' : disc.photoUrl ? '📷 Trocar foto' : '📷 Adicionar foto'}
                <input type="file" accept="image/*" style={{ display: 'none' }} disabled={uploadingPhoto} onChange={onPickPhoto} />
              </label>
            )}
          </div>
          <div className="disc-header-info">
            <div className="disc-kv"><span>Código interno</span><b>{disc.codigoInterno ?? '—'}</b></div>
            <div className="disc-kv"><span>Início</span><b>{inicio}</b></div>
            <div className="disc-kv"><span>Plano</span><b>{disc.planMonths} meses</b></div>
            <div className="disc-kv"><span>Responsável</span><b>{disc.responsavelNome ?? '—'}</b></div>
            <div className="disc-kv"><span>Cobertura embalagens</span><b>{disc.coberturaTempo ?? '—'}{disc.coberturaValor != null ? ` · ${brl(disc.coberturaValor)}` : ''}</b></div>
            {disc.motivo && <div className="disc-kv full"><span>Motivo</span><b>{disc.motivo}</b></div>}
            {disc.comentarios && <div className="disc-kv full"><span>Comentários</span><b>{disc.comentarios}</b></div>}
            {disc.outcomeComment && <div className="disc-kv full"><span>Tratativa</span><b>{disc.outcomeComment}</b></div>}
          </div>
        </div>

        {/* Timeline / gráficos */}
        <div className="card card-pad">
          <div className="section-title"><h2>Evolução dos indicadores</h2></div>
          <p className="muted" style={{ fontSize: 12.5, marginTop: -6 }}>
            A linha pontilhada é a meta do último mês — veja se cada indicador está se aproximando dela.
          </p>
          <div className="disc-charts">
            <IndicatorChart title="Faturamento" fmt={brl} data={serie((e) => e.faturamento)} meta={disc.metaFaturamento} months={months} />
            <IndicatorChart title="Margem de contribuição (%)" fmt={(v) => (v == null ? '—' : `${v}%`)} data={serie((e) => e.margemContribuicao)} meta={disc.metaMargem} months={months} />
            <IndicatorChart title="Clientes positivados" fmt={(v) => (v == null ? '—' : String(v))} data={serie((e) => e.clientesPositivados)} meta={disc.metaClientes} months={months} />
            <IndicatorChart title="% Devolução (NFD)" fmt={pct} data={serie((e) => e.nfd)} meta={disc.metaNfd} months={months} lowerIsBetter />
          </div>
        </div>

        {/* Lançamentos mensais */}
        <div className="card card-pad">
          <div className="section-title">
            <h2>Lançamentos mensais</h2>
            <span className="muted" style={{ fontSize: 12 }}>
              cada mês só é liberado <b>após o fechamento</b> (por volta do dia 10 do mês seguinte); meses passados podem ser preenchidos a qualquer momento (retroativo)
            </span>
          </div>
          <div className="table-wrap">
            <table className="disc-entries">
              <thead>
                <tr>
                  <th>Mês</th><th>Faturamento (R$)</th><th>MC [R] (%)</th><th>Clientes posit.</th><th>% Devolução (NFD)</th><th>Comentários</th><th></th>
                </tr>
              </thead>
              <tbody>
                {months.map((m) => (
                  <EntryRow
                    key={m}
                    month={m}
                    isThisMonth={m === thisMonth}
                    liberado={mesLiberado(m)}
                    podeAntecipar={podeAntecipar}
                    entry={byMonth.get(m) ?? null}
                    orgId={disc.orgId}
                    discId={disc.id}
                    userId={userId}
                    fullName={fullName}
                    onSaved={reload}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Metas (edição) */}
        {canManage && disc.status === 'em_acompanhamento' && (
          <MetasEditor disc={disc} onSaved={reload} />
        )}

        {/* Decisão final */}
        {canManage && disc.status === 'em_acompanhamento' && (
          <div className="card card-pad">
            <div className="section-title"><h2>Decisão</h2></div>
            <p className="muted" style={{ fontSize: 12.5, marginTop: -6 }}>
              Ao final do prazo (ou quando decidir), escolha o encaminhamento do SKU. Tudo fica no histórico.
            </p>
            <div className="disc-decisions">
              <button className="btn-ghost" onClick={() => setDecision('voltar_mix')}>↩︎ Voltar para o mix</button>
              <button className="btn-ghost" onClick={() => setDecision('prorrogacao')}>⏳ Prorrogar prazo</button>
              <button className="btn-ghost" onClick={() => setDecision('desativar')}>⛔ Desativar SKU</button>
            </div>
          </div>
        )}

        {/* Histórico */}
        <div className="card card-pad">
          <div className="section-title"><h2>Histórico de decisões</h2></div>
          {events.length === 0 ? (
            <p className="muted">Nenhuma decisão registrada ainda.</p>
          ) : (
            <div className="disc-history">
              {events.map((ev) => (
                <div className="disc-hist-item" key={ev.id}>
                  <div className="disc-hist-head">
                    <b>{EVENT_LABELS[ev.kind]}</b>
                    <span className="muted">{ev.byName ?? '—'} · {new Date(ev.createdAt).toLocaleString('pt-BR')}</span>
                  </div>
                  {ev.kind === 'prorrogacao' && (
                    <div className="muted" style={{ fontSize: 12.5 }}>
                      +{ev.monthsAdded ?? 0} meses
                      {ev.metaFaturamento != null ? ` · nova meta fat. ${brl(ev.metaFaturamento)}` : ''}
                      {ev.metaMargem != null ? ` · MC ${ev.metaMargem}%` : ''}
                      {ev.metaClientes != null ? ` · ${ev.metaClientes} clientes` : ''}
                      {ev.metaNfd != null ? ` · devolução ${pct(ev.metaNfd)}` : ''}
                    </div>
                  )}
                  {ev.comment && <div className="note-body">{ev.comment}</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Comentários / discussão (todos participam) */}
        <ComentariosSaida
          disc={disc}
          comments={comments}
          userId={userId}
          fullName={fullName}
          canManage={canManage}
          onChanged={reload}
        />

        {canManage && (
          <button
            className="linklike"
            style={{ color: 'var(--danger, #c0392b)' }}
            onClick={async () => {
              if (!confirm('Excluir este item e todo o seu histórico? Esta ação não pode ser desfeita.')) return;
              const { error } = await deleteDiscontinuation(disc.id);
              if (error) return alert(error);
              window.location.href = '/saida-linha';
            }}
          >
            Excluir item
          </button>
        )}
      </div>

      {decision && (
        <DecisionModal
          kind={decision}
          disc={disc}
          userId={userId}
          fullName={fullName}
          onClose={() => setDecision(null)}
          onDone={() => {
            setDecision(null);
            reload();
          }}
        />
      )}

      <FloatingBack to="/saida-linha" label="Voltar à lista" />
    </>
  );
}

const EVENT_LABELS: Record<DiscEventKind, string> = {
  prorrogacao: '⏳ Prazo prorrogado',
  desativar: '⛔ SKU desativado',
  voltar_mix: '↩︎ Voltou para o mix',
  nota: '📝 Nota',
};

function EntryRow({
  month, isThisMonth, liberado, podeAntecipar, entry, orgId, discId, userId, fullName, onSaved,
}: {
  month: string;
  isThisMonth: boolean;
  liberado: boolean; // o mês já fechou? (a partir do dia 10 do mês seguinte)
  podeAntecipar: boolean; // admin pode preencher antes do fechamento
  entry: DiscEntry | null;
  orgId: string;
  discId: string;
  userId: string | null;
  fullName: string | null;
  onSaved: () => void;
}) {
  const [fat, setFat] = useState(entry?.faturamento?.toString() ?? '');
  const [mc, setMc] = useState(entry?.margemContribuicao?.toString() ?? '');
  const [cli, setCli] = useState(entry?.clientesPositivados?.toString() ?? '');
  const [nfd, setNfd] = useState(entry?.nfd?.toString() ?? '');
  const [com, setCom] = useState(entry?.comentarios ?? '');
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const mark = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setDirty(true); };
  // Mês ainda em aberto (não fechou): bloqueado para todos, exceto admin.
  const travado = !liberado && !podeAntecipar;
  const rotulo = liberado
    ? ''
    : travado
      ? `🔒 libera em ${textoLiberacao(month)}`
      : `⏳ em aberto (libera em ${textoLiberacao(month)}; admin pode antecipar)`;

  async function salvar() {
    setBusy(true);
    const { error } = await upsertEntry({
      orgId,
      discontinuationId: discId,
      refMonth: month,
      faturamento: numOrNull(fat),
      margemContribuicao: numOrNull(mc),
      clientesPositivados: numOrNull(cli),
      nfd: numOrNull(nfd),
      comentarios: com.trim() || null,
      byId: userId,
      byName: fullName,
    });
    setBusy(false);
    if (error) return alert(error);
    setDirty(false);
    onSaved();
  }

  return (
    <tr className={isThisMonth ? 'disc-row-now' : ''}>
      <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
        {monthLabel(month)}
        {isThisMonth ? ' · mês em aberto' : ''}
        {rotulo && <div className="muted" style={{ fontWeight: 400, fontSize: 11 }}>{rotulo}</div>}
      </td>
      <td><input type="number" value={fat} disabled={travado} onChange={(e) => mark(setFat)(e.target.value)} /></td>
      <td><input type="number" value={mc} disabled={travado} onChange={(e) => mark(setMc)(e.target.value)} /></td>
      <td><input type="number" value={cli} disabled={travado} onChange={(e) => mark(setCli)(e.target.value)} /></td>
      <td><input type="number" value={nfd} disabled={travado} onChange={(e) => mark(setNfd)(e.target.value)} /></td>
      <td><input value={com} disabled={travado} onChange={(e) => mark(setCom)(e.target.value)} /></td>
      <td>
        {!travado && (
          <button className="btn-primary btn-sm" disabled={busy || !dirty} onClick={salvar}>
            {busy ? '…' : 'Salvar'}
          </button>
        )}
      </td>
    </tr>
  );
}

function MetasEditor({ disc, onSaved }: { disc: Discontinuation; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [fat, setFat] = useState(disc.metaFaturamento?.toString() ?? '');
  const [mc, setMc] = useState(disc.metaMargem?.toString() ?? '');
  const [cli, setCli] = useState(disc.metaClientes?.toString() ?? '');
  const [nfd, setNfd] = useState(disc.metaNfd?.toString() ?? '');
  const [busy, setBusy] = useState(false);

  async function salvar() {
    setBusy(true);
    const { error } = await updateDiscontinuation(disc.id, {
      meta_faturamento: numOrNull(fat),
      meta_margem: numOrNull(mc),
      meta_clientes: numOrNull(cli),
      meta_nfd: numOrNull(nfd),
    });
    setBusy(false);
    if (error) return alert(error);
    setOpen(false);
    onSaved();
  }

  if (!open) {
    return (
      <div className="card card-pad">
        <div className="section-title">
          <h2>Metas do último mês</h2>
          <button className="btn-ghost btn-sm" onClick={() => setOpen(true)}>✏️ Editar metas</button>
        </div>
        <div className="muted" style={{ fontSize: 13 }}>
          Faturamento {brl(disc.metaFaturamento)} · MC {disc.metaMargem != null ? `${disc.metaMargem}%` : '—'} · {disc.metaClientes != null ? `${disc.metaClientes} clientes` : '— clientes'} · Devolução {pct(disc.metaNfd)}
        </div>
      </div>
    );
  }
  return (
    <div className="card card-pad">
      <div className="section-title"><h2>Metas do último mês</h2></div>
      <div className="capa-grid">
        <label className="field"><span>Faturamento (R$)</span><input type="number" value={fat} onChange={(e) => setFat(e.target.value)} /></label>
        <label className="field"><span>Margem de contribuição (%)</span><input type="number" value={mc} onChange={(e) => setMc(e.target.value)} /></label>
        <label className="field"><span>Clientes positivados</span><input type="number" value={cli} onChange={(e) => setCli(e.target.value)} /></label>
        <label className="field"><span>% de devolução (NFD)</span><input type="number" step="0.1" value={nfd} onChange={(e) => setNfd(e.target.value)} /></label>
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="btn-ghost btn-sm" onClick={() => setOpen(false)}>Cancelar</button>
        <button className="btn-primary btn-sm" disabled={busy} onClick={salvar}>{busy ? 'Salvando…' : 'Salvar metas'}</button>
      </div>
    </div>
  );
}

function DecisionModal({
  kind, disc, userId, fullName, onClose, onDone,
}: {
  kind: DiscEventKind;
  disc: Discontinuation;
  userId: string | null;
  fullName: string | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [months, setMonths] = useState(3);
  const [fat, setFat] = useState(disc.metaFaturamento?.toString() ?? '');
  const [mc, setMc] = useState(disc.metaMargem?.toString() ?? '');
  const [cli, setCli] = useState(disc.metaClientes?.toString() ?? '');
  const [nfd, setNfd] = useState(disc.metaNfd?.toString() ?? '');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const title =
    kind === 'prorrogacao' ? 'Prorrogar prazo' : kind === 'desativar' ? 'Desativar SKU' : 'Voltar para o mix';

  async function confirmar() {
    setBusy(true);
    const { error } = await applyDecision({
      disc,
      kind,
      monthsAdded: kind === 'prorrogacao' ? months : null,
      metaFaturamento: kind === 'prorrogacao' ? numOrNull(fat) : null,
      metaMargem: kind === 'prorrogacao' ? numOrNull(mc) : null,
      metaClientes: kind === 'prorrogacao' ? numOrNull(cli) : null,
      metaNfd: kind === 'prorrogacao' ? numOrNull(nfd) : null,
      comment: comment.trim() || null,
      byId: userId,
      byName: fullName,
    });
    setBusy(false);
    if (error) return alert(error);
    onDone();
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal card" onClick={(e) => e.stopPropagation()}>
        <div className="section-title">
          <h2>{title}</h2>
          <button className="btn-ghost btn-sm" onClick={onClose}>Fechar</button>
        </div>
        <div className="modal-body">
          {kind === 'prorrogacao' && (
            <>
              <label className="field">
                <span>Quantos meses adicionar ao plano?</span>
                <input type="number" min={1} value={months} onChange={(e) => setMonths(Number(e.target.value))} />
              </label>
              <h3 className="capa-sub">Nova meta para o novo último mês (opcional)</h3>
              <div className="capa-grid">
                <label className="field"><span>Faturamento (R$)</span><input type="number" value={fat} onChange={(e) => setFat(e.target.value)} /></label>
                <label className="field"><span>Margem de contribuição (%)</span><input type="number" value={mc} onChange={(e) => setMc(e.target.value)} /></label>
                <label className="field"><span>Clientes positivados</span><input type="number" value={cli} onChange={(e) => setCli(e.target.value)} /></label>
                <label className="field"><span>% de devolução (NFD)</span><input type="number" step="0.1" value={nfd} onChange={(e) => setNfd(e.target.value)} /></label>
              </div>
            </>
          )}
          <label className="field">
            <span>Qual tratativa será dada? (comentário)</span>
            <textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
          </label>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn-ghost" onClick={onClose}>Cancelar</button>
            <button className="btn-primary" disabled={busy} onClick={confirmar}>{busy ? 'Salvando…' : 'Confirmar'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Comentários do item (todos participam) — histórico com data + autor.
function ComentariosSaida({
  disc, comments, userId, fullName, canManage, onChanged,
}: {
  disc: Discontinuation;
  comments: DiscComment[];
  userId: string | null;
  fullName: string | null;
  canManage: boolean;
  onChanged: () => void;
}) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  async function enviar() {
    if (!body.trim()) return;
    setBusy(true);
    const { error } = await addComment({
      orgId: disc.orgId,
      discontinuationId: disc.id,
      authorId: userId,
      authorName: fullName,
      body: body.trim(),
    });
    setBusy(false);
    if (error) return alert(error);
    setBody('');
    onChanged();
  }

  return (
    <div className="card card-pad">
      <div className="section-title">
        <h2>Comentários &amp; decisões</h2>
        <span className="muted" style={{ fontSize: 12 }}>todos podem comentar</span>
      </div>
      <div className="note-form">
        <textarea
          className="note-textarea"
          rows={3}
          placeholder="Escreva um comentário / registre uma decisão sobre este item…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <div className="note-actions">
          <button className="btn-primary btn-sm" disabled={busy || !body.trim()} style={{ marginLeft: 'auto' }} onClick={enviar}>
            {busy ? 'Enviando…' : 'Comentar'}
          </button>
        </div>
      </div>
      {comments.length === 0 ? (
        <p className="muted" style={{ marginTop: 8 }}>Nenhum comentário ainda.</p>
      ) : (
        <div className="note-list">
          {comments.map((c) => (
            <div className="note-item" key={c.id}>
              <div className="note-head">
                <span className="muted" style={{ fontSize: 11.5 }}>
                  <b>{c.authorName ?? '—'}</b> · {new Date(c.createdAt).toLocaleString('pt-BR', {
                    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
                  })}
                </span>
                {(c.authorId === userId || canManage) && (
                  <button
                    className="linklike"
                    onClick={async () => {
                      if (!confirm('Remover este comentário?')) return;
                      const { error } = await deleteComment(c.id);
                      if (error) return alert(error);
                      onChanged();
                    }}
                  >
                    remover
                  </button>
                )}
              </div>
              <div className="note-body">{c.body}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Gráfico de um indicador: linha dos valores por mês, linha da meta (pontilhada),
// linha do zero quando há valores negativos e o número ao lado de cada ponto.
function IndicatorChart({
  title, data, meta, months, fmt, lowerIsBetter = false,
}: {
  title: string;
  data: { m: string; v: number | null }[];
  meta: number | null;
  months: string[];
  fmt: (v: number | null) => string;
  lowerIsBetter?: boolean;
}) {
  const w = 320, h = 178, padL = 8, padR = 8, padT = 22, padB = 38;
  const vals = data.map((d) => d.v).filter((v): v is number => v != null);
  const allVals = meta != null ? [...vals, meta, 0] : [...vals, 0];
  const maxV = Math.max(1, ...allVals);
  const minV = Math.min(0, ...allVals);
  const span = maxV - minV || 1;
  const n = Math.max(1, months.length);
  const x = (i: number) => padL + (i * (w - padL - padR)) / Math.max(1, n - 1);
  const y = (v: number) => padT + (1 - (v - minV) / span) * (h - padT - padB);

  const pts = data
    .map((d, i) => (d.v == null ? null : `${x(i)},${y(d.v)}`))
    .filter((p): p is string => p != null)
    .join(' ');
  const lastVal = [...data].reverse().find((d) => d.v != null)?.v ?? null;
  const hasNeg = minV < 0;

  return (
    <div className="disc-chart">
      <div className="disc-chart-title">{title}</div>
      <svg viewBox={`0 0 ${w} ${h}`} className="disc-chart-svg" role="img" aria-label={title}>
        {/* linha do zero (quando há valores negativos) */}
        {hasNeg && (
          <>
            <line x1={padL} x2={w - padR} y1={y(0)} y2={y(0)} stroke="var(--ink-faint, #9aa)" strokeWidth={1} />
            <text x={padL} y={y(0) - 2} fontSize="8" fill="var(--ink-faint, #9aa)">0</text>
          </>
        )}
        {/* meta */}
        {meta != null && (
          <>
            <line x1={padL} x2={w - padR} y1={y(meta)} y2={y(meta)} stroke="var(--brand-green, #2e7d32)" strokeWidth={1.5} strokeDasharray="4 3" />
            <text x={w - padR} y={y(meta) - 3} textAnchor="end" fontSize="9" fill="var(--brand-green, #2e7d32)">meta {fmt(meta)}</text>
          </>
        )}
        {/* linha dos valores */}
        {pts && <polyline points={pts} fill="none" stroke="var(--accent, #2563eb)" strokeWidth={2} />}
        {data.map((d, i) => {
          if (d.v == null) return null;
          const neg = d.v < 0;
          const labelY = neg ? y(d.v) + 11 : y(d.v) - 6; // valor negativo: rótulo embaixo
          return (
            <g key={d.m}>
              <circle cx={x(i)} cy={y(d.v)} r={3} fill={neg ? 'var(--st-atrasada, #c0392b)' : 'var(--accent, #2563eb)'} />
              <text x={x(i)} y={labelY} textAnchor="middle" fontSize="8.5" fontWeight="600"
                fill={neg ? 'var(--st-atrasada, #c0392b)' : 'var(--ink, #222)'}>
                {fmt(d.v)}
              </text>
            </g>
          );
        })}
        {/* eixos de mês (inclinados p/ não sobrepor) */}
        {months.map((m, i) => {
          const lx = x(i), ly = h - padB + 11;
          return (
            <text key={m} x={lx} y={ly} textAnchor="end" fontSize="8" fill="var(--ink-faint, #888)" transform={`rotate(-40 ${lx} ${ly})`}>
              {monthLabel(m)}
            </text>
          );
        })}
      </svg>
      <div className="disc-chart-foot">
        Último: <b>{fmt(lastVal)}</b>
        {meta != null ? <> · meta <b>{fmt(meta)}</b>{lowerIsBetter ? ' (quanto menor, melhor)' : ''}</> : null}
      </div>
    </div>
  );
}
